import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence, useDragControls, useReducedMotion } from 'framer-motion';
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import { useNavigate } from 'react-router-dom';
import Page, { readStoredStatus } from '../molecules/Page';
import api from '../../api/apiHelper';
import { readApiCache, writeApiCache } from '../../utils/apiCache';
import { isAccessBlocked, isNoAccessError } from '../../utils/access';
import axios from 'axios';
import { AUTH_TOKEN_KEY } from '../../utils/dashboardUtils';
import AttemptSummaryModal from '../molecules/AttemptSummaryModal';
import PaywallPopup from "../molecules/PaywallPopup";
import SessionSetupPopup from "../molecules/SessionSetupPopup";

import { AiOutlineCheckCircle, AiOutlineSync } from 'react-icons/ai';
import {
    FaGraduationCap,
    FaBolt,
    FaRegSave,
    FaRegCheckSquare,
    FaRegLightbulb
} from 'react-icons/fa';
import { FiChevronRight, FiChevronDown, FiX, FiBookOpen } from 'react-icons/fi';

const apiUrl = process.env.REACT_APP_API_SOURCE || "https://matematly.pl/api/";

const LOADING_STEPS = ['rozwiązujemy zadania', 'przeszukujemy kartę wzorów CKE', 'przygotowujemy Orła AI'];

const formatTimestamp = (unixTimestamp) => {
    if (!unixTimestamp) return '';
    const date = new Date(unixTimestamp * 1000);
    return date.toLocaleString('pl-PL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const pluralProba = (n) => {
    if (n === 1) return 'próba';
    if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return 'próby';
    return 'prób';
};

const ExamBenefits = () => (
    <div className="flex flex-col gap-2.5 mb-5">
        <div className="flex items-center gap-2.5">
            <FaRegLightbulb className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
            <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Praca nad błędami:</strong> Masz 3 zadania treningowe + dodaj temat do Treningu Dowolnego.</p>
        </div>
        {/* <div className="flex items-center gap-2.5">
            <FaRegCheckSquare className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
            <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Zadania otwarte:</strong> Kryteria oceniania na podstwie CKE.</p>
        </div> */}
        <div className="flex items-center gap-2.5">
            <FaRegSave className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
            <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Zapisuj postępy:</strong> Wracaj do arkusza kiedy chcesz i ile razy chcesz.</p>
        </div>
    </div>
);

const ExamSheetDetailSheet = ({
    sheet, attempts, loadingAttempts, actionLoading,
    onClose, onStartNew, onResume, onViewSummary
}) => {
    const sheetRef = useRef(sheet);
    if (sheet) sheetRef.current = sheet;
    const currentSheet = sheetRef.current;
    const dragControls = useDragControls();
    const prefersReducedMotion = useReducedMotion();
    const slideIn = useSheetSlideIn(!!(sheet && currentSheet));

    useEffect(() => {
        if (sheet) {
            window.dispatchEvent(new CustomEvent('bottomsheet:open'));
            return () => window.dispatchEvent(new CustomEvent('bottomsheet:close'));
        }
    }, [sheet]);

    return ReactDOM.createPortal(
        <AnimatePresence>
            {sheet && currentSheet && (
                <>
                    <motion.div
                        key="exam-backdrop"
                        {...getSheetBackdropMotion()}
                        className="fixed inset-0 bg-black/50 z-[9990]"
                        onClick={onClose}
                    />
                    <motion.div
                        key="exam-sheet"
                        {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
                        drag="y"
                        dragControls={dragControls}
                        dragListener={false}
                        dragConstraints={{ top: 0 }}
                        dragElastic={{ top: 0, bottom: 0.4 }}
                        onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
                        className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
                    >
                        <div
                            className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
                            onPointerDown={(e) => dragControls.start(e)}
                        >
                            <div className="w-10 h-1 bg-gray-200 rounded-full" />
                        </div>
                        <div className="flex items-center gap-3 px-5 pt-2 pb-4 border-b border-gray-100 flex-shrink-0">
                            <div className="flex-1 min-w-0">
                                <h2 className="font-apotek tracking-[0.05em] text-[16px] uppercase leading-none text-[#233855] truncate">
                                    {currentSheet.name}
                                </h2>
                                <p className="text-xs text-gray-400 mt-1">
                                    CKE · {currentSheet.year} · Formuła {currentSheet.formula}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
                            >
                                <FiX size={16} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-24">

                            {/* Banner informacyjny */}
                            <div className="flex items-center gap-3 bg-gradient-to-r from-[#233855]/5 to-transparent border border-[#233855]/10 rounded-xl px-3.5 py-3 mb-4">
                                <div className="w-8 h-8 rounded-xl bg-[#233855] flex items-center justify-center flex-shrink-0 shadow-md">
                                    <FiBookOpen size={15} className="text-[#E09F2E]" />
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold text-[#233855] leading-none">Oficjalny arkusz CKE</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Rozwiązania i trening od Matematly</p>
                                </div>
                            </div>

                            <button
                                onClick={onStartNew}
                                disabled={actionLoading === 'new'}
                                className="w-full bg-[#233855] hover:bg-[#2a4263] text-white font-apotek tracking-widest uppercase text-sm py-3 rounded-xl mb-5 transition-colors disabled:opacity-50 active:scale-[0.98]"
                            >
                                {actionLoading === 'new' ? 'Ładowanie...' : 'Rozwiązuj'}
                            </button>

                            {loadingAttempts ? (
                                <p className="text-sm text-gray-500 text-center py-4">Ładowanie prób...</p>
                            ) : attempts.length > 0 ? (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Poprzednie próby</p>
                                    <div className="space-y-2">
                                        {attempts.map(attempt => (
                                            <div key={attempt.attempt_id} className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <span className={`font-medium text-sm flex items-center gap-1.5 ${attempt.status === 'in_progress' ? 'text-[#233855]' : 'text-gray-700'}`}>
                                                            {attempt.status === 'in_progress'
                                                                ? <AiOutlineSync size={14} />
                                                                : <AiOutlineCheckCircle size={14} className="text-[#007D00]" />
                                                            }
                                                            {attempt.status === 'in_progress' ? 'W toku' : 'Ukończono'}
                                                        </span>
                                                        <p className="text-xs text-gray-400 mt-0.5">{formatTimestamp(attempt.started_at)}</p>
                                                    </div>
                                                    <div className="flex gap-1.5 flex-shrink-0">
                                                        {attempt.status === 'completed' && (
                                                            <button
                                                                onClick={() => onViewSummary(attempt.attempt_id)}
                                                                className="font-apotek uppercase bg-[#007D00] text-white px-2.5 py-1.5 rounded-lg text-[11px] tracking-wider hover:bg-[#007D00]/90 transition-colors"
                                                            >
                                                                Wyniki
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => onResume(attempt.attempt_id)}
                                                            disabled={actionLoading === attempt.attempt_id}
                                                            className={`font-apotek uppercase px-2.5 py-1.5 rounded-lg text-[11px] tracking-wider text-white transition-colors disabled:bg-gray-400 ${
                                                                attempt.status === 'in_progress'
                                                                    ? 'bg-[#233855] hover:bg-[#2a4263]'
                                                                    : 'bg-[#E09F2E] hover:bg-yellow-600'
                                                            }`}
                                                        >
                                                            {actionLoading === attempt.attempt_id ? '...' : (attempt.status === 'in_progress' ? 'Wznów' : 'Zobacz')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-[11px] text-gray-400 text-center py-2">Pierwsze podejście — powodzenia!</p>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
};

const ExamSheetStarter = ({ setExamAttemptData }) => {
    const [sheets, setSheets] = useState(() => readApiCache('exams', Infinity) || []);
    const [loading, setLoading] = useState(!readApiCache('exams', Infinity));
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const [selectedSheet, setSelectedSheet] = useState(null);
    const [sheetAttempts, setSheetAttempts] = useState([]);
    const [loadingAttempts, setLoadingAttempts] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [summaryAttemptId, setSummaryAttemptId] = useState(null);

    const [isPaywallPopupOpen, setPaywallPopupOpen] = useState(false);
    const [isBenefitsOpen, setIsBenefitsOpen] = useState(() => {
        const saved = localStorage.getItem('exams_benefits_open');
        return saved !== null ? saved === 'true' : true;
    });
    const [accountStatus, setAccountStatus] = useState(readStoredStatus);
    const [popupUserData] = useState(() => {
        try { return JSON.parse(localStorage.getItem("paywallUserData")); } catch { return null; }
    });

    // Loading overlay sesji (Orzeł + teksty, jak na Dashboardzie)
    const [isSessionLoading, setIsSessionLoading] = useState(false);
    const [isSessionDataReady, setIsSessionDataReady] = useState(false);
    const [sessionMessages, setSessionMessages] = useState(null);
    const sessionDataRef = useRef(null);

    useEffect(() => {
        localStorage.setItem('exams_benefits_open', String(isBenefitsOpen));
    }, [isBenefitsOpen]);

    useEffect(() => {
        setExamAttemptData(null);
        localStorage.removeItem('examAttemptData');
        const fetchSheets = async () => {
            try {
                const { data } = await api.get('exams/', { skipLoading: true });
                setSheets(data);
                writeApiCache('exams', data);
            } catch (error) {
                if (!readApiCache('exams', Infinity)) setError("Nie udało się załadować arkuszy. Spróbuj ponownie później.");
            } finally {
                setLoading(false);
            }
        };
        fetchSheets();
    }, [setExamAttemptData]);

    const handleSheetClick = async (sheet) => {
        setSelectedSheet(sheet);
        setSheetAttempts([]);
        setLoadingAttempts(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setLoadingAttempts(false); return; }
        try {
            const response = await axios.get(
                `${apiUrl}exams/sheet/${sheet.sheet_id}/attempts/`,
                { headers: { Authorization: `Token ${token}` } }
            );
            setSheetAttempts(response.data);
        } catch (error) {
            console.error("Błąd pobierania prób:", error);
        } finally {
            setLoadingAttempts(false);
        }
    };

    // Wspólny start: zamyka bottom-sheet, odpala overlay (Orzeł + teksty), nawiguje po załadowaniu
    const startExamAttempt = (promise) => {
        const sheetName = selectedSheet?.name;
        setSelectedSheet(null);
        setSessionMessages(sheetName
            ? [`Otwieramy arkusz: ${sheetName}`, ...LOADING_STEPS]
            : LOADING_STEPS);
        setIsSessionLoading(true);
        setIsSessionDataReady(false);
        sessionDataRef.current = null;
        promise
            .then(({ data }) => { sessionDataRef.current = data; setIsSessionDataReady(true); })
            .catch(error => {
                setIsSessionLoading(false);
                // 403 (stara sciezka) lub 402 z bramki poziomu arkusza.
                if ((error.response && error.response.status === 403) || isNoAccessError(error)) {
                    setPaywallPopupOpen(true);
                } else {
                    console.error("Błąd ładowania arkusza:", error);
                }
            });
    };

    const handleStartNewAttempt = (sheetId) => {
        if (isAccessBlocked(accountStatus)) {
            setPaywallPopupOpen(true);
            return;
        }
        startExamAttempt(api.post('exams/attempt/start_new/', { sheet_id: sheetId }, { skipLoading: true }));
    };

    const handleResumeAttempt = (attemptId) => {
        startExamAttempt(api.post('exams/attempt/load/', { attempt_id: attemptId }, { skipLoading: true }));
    };

    // Overlay zostaje do nawigacji — navigate odmontuje ekran razem z popupem w jednym commicie
    const handleExamSessionComplete = () => {
        const data = sessionDataRef.current;
        sessionDataRef.current = null;
        if (data) {
            setExamAttemptData(data);
            localStorage.setItem('examAttemptData', JSON.stringify(data));
            navigate('/solve-exam-sheet');
        } else {
            setIsSessionLoading(false);
            setIsSessionDataReady(false);
        }
    };

    const handleSubscribeClick = () => { setPaywallPopupOpen(false); navigate("/upgrade"); };

    const handleClaimGracePeriod = async () => {
        try {
            const { data } = await api.post("activate-grace-period/");
            if (data.status === "success") {
                setPaywallPopupOpen(false);
                const updatedStatus = { ...accountStatus, has_access: true, can_activate_grace_period: false };
                setAccountStatus(updatedStatus);
                localStorage.setItem("userStatus", JSON.stringify(updatedStatus));
            }
        } catch (error) {
            console.error("Błąd aktywacji grace period:", error);
        }
    };

    const renderContent = () => {
        if (loading) return <div className="text-center p-10 text-gray-600">Ładowanie arkuszy...</div>;
        if (error) return <div className="text-center p-10 text-red-600">{error}</div>;
        if (sheets.length === 0) return <div className="text-center p-10 text-gray-600">Brak dostępnych arkuszy.</div>;

        return (
            <div>
                {sheets.map(sheet => {
                    const cnt = sheet.user_attempt_count || 0;
                    return (
                        <button
                            key={sheet.sheet_id}
                            type="button"
                            onClick={() => handleSheetClick(sheet)}
                            className="w-full text-left p-3.5 mb-2.5 bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.97] transition-all duration-150 group flex items-center gap-3"
                        >
                            <div className="flex-shrink-0 p-2 rounded-xl shadow-md bg-[#233855]">
                                <FaGraduationCap className="w-4 h-4 text-[#E09F2E]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="font-semibold text-sm text-[#233855] leading-snug truncate">{sheet.name}</h2>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    CKE{cnt > 0 ? ` · ${cnt} ${pluralProba(cnt)}` : ''}
                                </p>
                            </div>
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
                                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <Page showNav accountStatus={accountStatus}>
            <AttemptSummaryModal
                isOpen={!!summaryAttemptId}
                onClose={() => setSummaryAttemptId(null)}
                attemptId={summaryAttemptId}
            />
            <PaywallPopup
                isOpen={isPaywallPopupOpen}
                onClose={() => setPaywallPopupOpen(false)}
                onSubscribe={handleSubscribeClick}
                userData={popupUserData}
                canClaimGracePeriod={accountStatus?.can_activate_grace_period}
                onClaimGracePeriod={handleClaimGracePeriod}
            />
            <ExamSheetDetailSheet
                sheet={selectedSheet}
                attempts={sheetAttempts}
                loadingAttempts={loadingAttempts}
                actionLoading={actionLoading}
                onClose={() => setSelectedSheet(null)}
                onStartNew={() => handleStartNewAttempt(selectedSheet?.sheet_id)}
                onResume={handleResumeAttempt}
                onViewSummary={(id) => { setSelectedSheet(null); setSummaryAttemptId(id); }}
            />

            <SessionSetupPopup
                isOpen={isSessionLoading}
                isDataReady={isSessionDataReady}
                onComplete={handleExamSessionComplete}
                messages={sessionMessages}
            />

            <div className="bg-white lg:bg-transparent w-full">
                <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
                    <div className="w-full lg:px-10 py-4 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200 flex flex-col gap-5">
                        <div className="text-center">
                            <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none">
                                Inteligentne Arkusze
                            </h1>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={() => setIsBenefitsOpen(v => !v)}
                                className="flex items-center justify-between w-full mb-2 group"
                            >
                                <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">Dlaczego to działa?</span>
                                <FiChevronDown className={`text-gray-300 group-hover:text-gray-500 transition-all duration-200 ${isBenefitsOpen ? 'rotate-180' : ''}`} size={14} />
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isBenefitsOpen ? 'max-h-52 opacity-100' : 'max-h-0 opacity-0'}`}>
                                <ExamBenefits />
                            </div>
                        </div>
                        {renderContent()}
                    </div>
                    <div className="h-[13vh] flex-shrink-0" />
                </div>
            </div>
        </Page>
    );
};

export default ExamSheetStarter;
